/* Kkumi Club Launcher — sitewide floating rewards widget.
 *
 * A pink FAB (bottom-right by default) opens a compact panel that surfaces the
 * member's Kkumi Club status and four actions — earn, redeem, my rewards, refer
 * a friend — with an in-panel "view" router (no page change). Guests see a
 * join-gated "hybrid" card.
 *
 * DATA is LIVE from the rewards app, read through the shared window.KKRewards
 * cache (assets/kkumi-rewards-cache.js): /program (public), /balance, /codes,
 * /dashboard, /options, /referral. The cache version-gates member reads on a
 * single cheap /balance probe per page load, so once the data is warmed it is
 * served from sessionStorage until the point balance or reward count changes.
 *
 * PREFETCH: for members the panel's data is warmed in the background (on idle)
 * before it's ever opened, so opening is instant. It also re-checks on tab
 * refocus. If KKRewards is unavailable it falls back to direct fetches.
 *
 * SECURITY (FRONTEND.md): every API-controlled value is escaped before it
 * touches innerHTML; URLs are allow-listed (path or http[s]); the only
 * innerHTML'd markup is the worker-sanitised tier icon_svg. Async buttons use
 * window.KKBtn busy states.
 */
(function () {
  'use strict';
  if (window.__kkumiLauncherInit) return;
  window.__kkumiLauncherInit = true;

  function boot() {
    var CFG = window.KKUMI_LAUNCHER || {};
    if (CFG.enabled === false) return;
    var CLUB = window.KKUMI_CLUB || {};
    var IS_MEMBER = !!CLUB.isMember;

    // Cart page treatment (theme settings -> Kkumi Club Launcher -> "On the
    // cart page"). The cart's wallet hero already states the balance, so the
    // wide points pill duplicates it right next to the checkout button:
    //   hidden    - don't mount at all here
    //   collapsed - always the plain round monogram (no points pill, DEFAULT)
    //   full      - the sitewide behaviour
    var CART_MODE = CFG.cartMode || 'collapsed';
    var CART_COLLAPSED = !!CFG.onCart && CART_MODE === 'collapsed';
    if (CFG.onCart && CART_MODE === 'hidden') return;

    var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var BASE = ROOT.replace(/\/$/, '');
    var REGISTER = CLUB.register || '/account/register';
    // Points and rewards are a Kkumi Club perk. A signed-in customer who hasn't
    // joined (/balance → club_member:false) gets the guest panels, but their
    // "join" CTAs go to the club signup (theme setting "Join the club link"),
    // not to sign-in — they're already signed in.
    var JOIN_URL = CLUB.joinUrl || '/pages/kkumi-club#kkumi-club-join';
    function joinHref() { return IS_MEMBER && !state.member ? JOIN_URL : REGISTER; }

    // ── helpers ──────────────────────────────────────────────────────────────
    var esc = window.KKEsc; // canonical shared escaper (assets/kkumi-esc.js)
    function safeUrl(u) {
      u = String(u == null ? '' : u);
      // http(s), root-relative, or a same-page anchor (the default join link,
      // #kkumi-club-join, jumps to the footer club signup).
      return (/^https?:\/\//i.test(u) || /^\/(?!\/)/.test(u) || /^#[A-Za-z][\w-]*$/.test(u)) ? u : '#';
    }
    function fmt(n) { return Number(n || 0).toLocaleString(); }
    // Read through the shared cache when present; fall back to a direct fetch.
    function get(path, opts) {
      if (window.KKRewards) return window.KKRewards.get(path, opts);
      return fetch(BASE + '/apps/rewards' + path, { credentials: 'same-origin' })
        .then(function (r) { return r.json().then(function (j) { if (!r.ok) { var e = new Error(j.error); e.code = j.error; throw e; } return j; }); });
    }
    function peek(path) { return window.KKRewards ? window.KKRewards.peek(path) : null; }
    function postApi(path, body) {
      return fetch(BASE + '/apps/rewards' + path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body) })
        .then(function (r) { return r.json().then(function (j) { if (!r.ok) { var e = new Error(j.error); e.code = j.error; throw e; } return j; }); })
        .then(function (d) { if (window.KKRewards) window.KKRewards.invalidate(); return d; });
    }
    function copy(key, fallback) { return CFG[key] != null && CFG[key] !== '' ? CFG[key] : fallback; }
    function idle(fn) { if (window.requestIdleCallback) requestIdleCallback(fn, { timeout: 3000 }); else setTimeout(fn, 1200); }
    // Editable-copy helpers. tpl() escapes the merchant string (settings are
    // trusted store config, but escaping keeps innerHTML safe by default) then
    // substitutes {tokens} with already-trusted/escaped fragments (live numbers,
    // pre-built <b>/<em> spans). rawTpl() skips escaping for the two strings that
    // deliberately carry merchant <em>/<br> markup (the refer headline). ptsHtml()
    // is the shared "<b>N pts</b>" with the editable unit word.
    function tpl(key, fallback, vars) {
      var s = esc(copy(key, fallback));
      if (vars) for (var k in vars) s = s.split('{' + k + '}').join(vars[k]);
      return s;
    }
    function rawTpl(key, fallback, vars) {
      var s = String(copy(key, fallback));
      if (vars) for (var k in vars) s = s.split('{' + k + '}').join(vars[k]);
      return s;
    }
    function ptsHtml(n) { return '<b>' + fmt(n) + ' ' + esc(copy('ptsUnit', 'pts')) + '</b>'; }

    // ── icons (sized inline SVGs — width/height baked in so they never blow up) ─
    var ICONS = {
      spark: '<path d="M12 3v6M9 6h6M6 13v6M3 16h6M17 11l1.6 3.4L22 16l-3.4 1.6L17 21l-1.6-3.4L12 16l3.4-1.6z"/>',
      ticket: '<path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4z"/><path d="M14 6v12" stroke-dasharray="2 2.4"/>',
      heart: '<path d="M19.5 5.5a4.4 4.4 0 0 0-6.2 0L12 6.8l-1.3-1.3a4.4 4.4 0 1 0-6.2 6.2L12 19l7.5-7.3a4.4 4.4 0 0 0 0-6.2z"/>',
      users: '<path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM20 19v-1.5a3.5 3.5 0 0 0-2.6-3.4M15 4.2a3.5 3.5 0 0 1 0 6.6"/>',
      tag: '<path d="M3 11.5V4a1 1 0 0 1 1-1h7.5a1 1 0 0 1 .7.3l8.5 8.5a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 12.2a1 1 0 0 1-.3-.7Z"/><circle cx="7.5" cy="7.5" r="1.4"/>',
      star: '<path d="m12 3 2.6 5.6 6.1.7-4.5 4.1 1.2 6L12 16.9 6.6 19.4l1.2-6-4.5-4.1 6.1-.7z"/>',
      gift: '<path d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
      lock: '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
      arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
      back: '<path d="M15 6l-6 6 6 6"/>',
      close: '<path d="M6 6l12 12M18 6 6 18"/>',
      copy: '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h8"/>',
      share: '<circle cx="6" cy="12" r="2.6"/><circle cx="17" cy="6" r="2.6"/><circle cx="17" cy="18" r="2.6"/><path d="M8.3 10.9 14.7 7.2M8.3 13.1l6.4 3.7"/>',
    };
    function ic(name, size) {
      size = size || 18;
      return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICONS[name] || '') + '</svg>';
    }
    // Tier icon: the admin's per-tier SVG (worker-sanitised → trusted, uses
    // currentColor so it picks up the surrounding text colour), else a FILLED
    // star so it never reads as a hollow outline.
    function tierIcon(tierId, size) {
      size = size || 14;
      var t = tierId && state.tiersById[tierId];
      if (t && t.icon_svg) return '<span class="kkl-ticon" style="width:' + size + 'px;height:' + size + 'px">' + t.icon_svg + '</span>';
      return '<svg class="kkl-ticon" width="' + size + '" height="' + size + '" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path fill="currentColor" d="m12 2 2.9 6.2 6.8.7-5 4.6 1.3 6.8L12 17.8 5.9 21l1.3-6.8-5-4.6 6.8-.7z"/></svg>';
    }

    // ── state ────────────────────────────────────────────────────────────────
    var state = {
      open: false, view: 'home', member: IS_MEMBER,
      program: null, tiersById: {},
      balance: null, dashboard: null, codes: null, options: null, referral: null,
      loading: {}, error: {},
    };
    function balanceNow() {
      if (state.dashboard && state.dashboard.points) return state.dashboard.points.balance;
      if (state.balance) return state.balance.balance || 0;
      return null;
    }
    function applyProgram(p) {
      state.program = p; state.tiersById = {};
      (p.tiers || []).forEach(function (t) { state.tiersById[t.id] = t; });
    }

    // ── DOM scaffold ───────────────────────────────────────────────────────────
    var rootEl = document.createElement('div');
    rootEl.className = 'kkumi-launcher' + (CFG.position === 'bottom-left' ? ' kkumi-launcher--left' : '');
    rootEl.setAttribute('data-open', '0');
    if (CFG.accent) {
      rootEl.style.setProperty('--kk-primary-fill', CFG.accent);
      rootEl.style.setProperty('--kk-primary-hover-fill', CFG.accent);
      rootEl.style.setProperty('--kk-pink-hot', CFG.accent);
      rootEl.style.setProperty('--kk-pink-deep', CFG.accent);
    }
    rootEl.innerHTML =
      '<div class="kkl-dim" data-kkl="close"></div>' +
      '<div class="kkl-dock">' +
        '<div class="kkl-panel" role="dialog" aria-modal="false" aria-label="Kkumi Club" hidden></div>' +
        '<button type="button" class="kkl-fab kkl-fab--round" aria-label="Kkumi Club" aria-expanded="false" data-kkl="toggle">' +
          '<span class="kkl-fab__face"><img class="kkl-fab__logo" src="' + esc(CFG.logo || '') + '" alt=""></span>' +
        '</button>' +
      '</div>';
    var panelEl = rootEl.querySelector('.kkl-panel');
    var fabEl = rootEl.querySelector('.kkl-fab');
    var toastEl = document.createElement('div');
    toastEl.className = 'kkl-toast';
    toastEl.setAttribute('aria-live', 'polite');

    function mount() { document.body.appendChild(rootEl); document.body.appendChild(toastEl); renderFab(); }

    var toastTimer;
    function toast(html) {
      toastEl.innerHTML = html; toastEl.classList.add('is-on');
      clearTimeout(toastTimer); toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, 6000);
    }

    // ── FAB ────────────────────────────────────────────────────────────────────
    function renderFab() {
      var pts = balanceNow();
      // Members can opt to hide the monogram and show just their points pill.
      // On a collapsed cart FAB neither the points pill nor the icon-less
      // member variant applies — it is always the round monogram.
      var hideIcon = !CART_COLLAPSED && state.member && !!CFG.hideIconMember;
      var wide = !CART_COLLAPSED && state.member && (pts != null || hideIcon);
      fabEl.className = 'kkl-fab kkl-fab--' + (wide ? 'wide' : 'round') + (hideIcon ? ' kkl-fab--noicon' : '');
      fabEl.setAttribute('aria-expanded', state.open ? 'true' : 'false');
      var html = '';
      if (!hideIcon) html += '<span class="kkl-fab__face"><img class="kkl-fab__logo" src="' + esc(CFG.logo || '') + '" alt=""></span>';
      if (wide) {
        html += (pts != null)
          ? '<span class="kkl-fab__label">' + fmt(pts) + '<small> pts</small></span>'
          : '<span class="kkl-fab__label">' + esc(copy('fabLabel', 'Rewards')) + '</span>';
      }
      var ping = state.member && state.codes ? state.codes.length : 0;
      if (ping) html += '<span class="kkl-fab__ping">' + esc(ping) + '</span>';
      fabEl.innerHTML = html;
    }

    // ── open / close / navigate ──────────────────────────────────────────────
    function setOpen(v) {
      state.open = v;
      rootEl.setAttribute('data-open', v ? '1' : '0');
      fabEl.setAttribute('aria-expanded', v ? 'true' : 'false');
      if (v) {
        panelEl.hidden = false;
        if (state.member && !state.dashboard && !state.loading.dashboard) loadDashboard();
        render();
        var f = panelEl.querySelector('button, a, input, [tabindex]');
        if (f) try { f.focus(); } catch (e) {}
      } else {
        try { fabEl.focus(); } catch (e) {}
        setTimeout(function () { if (!state.open) panelEl.hidden = true; }, 240);
      }
    }
    function go(view) {
      state.view = view;
      // Held options paint at once; the loader refreshes them in the background
      // (a no-op cache hit when nothing changed) so edits made in the admin or
      // a redemption elsewhere show up without a spinner.
      if (view === 'redeem' && !state.loading.options) loadOptions();
      if (view === 'rewards' && state.member && !state.codes && !state.loading.codes) loadCodes();
      if (view === 'refer' && state.member && !state.referral && !state.loading.referral) loadReferral();
      render();
      var h = panelEl.querySelector('.kkl-back, .kkl-x');
      if (h) try { h.focus(); } catch (e) {}
    }

    // ── data loaders (all via the shared cache) ───────────────────────────────
    function loadProgram() {
      var cached = peek('/program'); if (cached) applyProgram(cached);
      return get('/program').then(function (p) { applyProgram(p); if (state.open) render(); }).catch(function () {});
    }
    // Signed in but not in the club → the guest experience (see JOIN_URL).
    // An older worker without the field means "member" (pre-club-gate).
    function applyClub(b) {
      if (b && b.logged_in && b.club_member === false) state.member = false;
    }
    function loadBalance() {
      return get('/balance').then(function (b) {
        applyClub(b);
        if (b && b.logged_in) state.balance = b;
        // Re-render whichever panel is open, not just home: the balance carries
        // the tier multiplier the earn panel's per-$1 rate is computed from.
        renderFab(); if (state.open) render();
      }).catch(function () {});
    }
    // Loaders set the loading flag ONLY when there's nothing to show yet — so a
    // cached/prefetched panel refreshes silently in the background instead of
    // flashing a spinner on every visit.
    function loadCodes() {
      if (!state.codes) { state.loading.codes = true; state.error.codes = false; if (state.open && state.view === 'rewards') render(); }
      return get('/codes').then(function (d) {
        state.codes = d.codes || []; state.loading.codes = false;
        renderFab(); if (state.open) render();
      }).catch(function () { state.loading.codes = false; if (!state.codes) state.error.codes = true; if (state.open) render(); });
    }
    function loadDashboard() {
      if (!state.dashboard) { state.loading.dashboard = true; state.error.dashboard = false; if (state.open && state.view === 'home') render(); }
      // /dashboard carries order-tracking that can move without a balance change,
      // so cap its reuse with a short TTL on top of the version gate.
      return get('/dashboard', { ttl: 120000 }).then(function (d) {
        if (d && d.logged_in) state.dashboard = d;
        state.loading.dashboard = false; renderFab(); if (state.open) render();
      }).catch(function () { state.loading.dashboard = false; if (!state.dashboard) state.error.dashboard = true; if (state.open) render(); });
    }
    function loadOptions() {
      if (!state.options) { state.loading.options = true; state.error.options = false; if (state.open && state.view === 'redeem') render(); }
      state.loading.options = true;
      if (state.member) idle(function () { ensureSheet().then(function () { if (state.open && state.view === 'redeem') render(); }, function () {}); });
      return get('/options').then(function (d) {
        state.options = d.options || []; state.loading.options = false; if (state.open) render();
      }).catch(function () { state.loading.options = false; if (!state.options) state.error.options = true; if (state.open) render(); });
    }
    function loadReferral() {
      if (!state.referral) { state.loading.referral = true; state.error.referral = false; if (state.open && state.view === 'refer') render(); }
      return get('/referral').then(function (d) {
        state.referral = d; state.loading.referral = false; if (state.open) render();
      }).catch(function () { state.loading.referral = false; if (!state.referral) state.error.referral = true; if (state.open) render(); });
    }
    // Pull last-known values from the shared cache synchronously so a panel
    // paints instantly instead of showing a spinner while the (often cached)
    // refresh resolves.
    function hydrateFromCache() {
      var o;
      if (!state.program && (o = peek('/program'))) applyProgram(o);
      if (!state.balance && (o = peek('/balance')) && o.logged_in) { applyClub(o); state.balance = o; }
      if (!state.codes && (o = peek('/codes'))) state.codes = o.codes || [];
      if (!state.options && (o = peek('/options'))) state.options = o.options || [];
      if (!state.referral && (o = peek('/referral'))) state.referral = o;
      if (!state.dashboard && (o = peek('/dashboard')) && o.logged_in) state.dashboard = o;
    }

    // Warm the member data in the background so the panel opens instantly. The
    // shared cache makes these no-ops (served from sessionStorage) whenever the
    // version is unchanged, so this is cheap on repeat page loads.
    function prefetch() {
      // Guests (and signed-in non-members) see the catalogue too, so warm it
      // for them as well — otherwise "Ways to redeem" waited on the /balance
      // probe + /options round trips only once the panel was already open.
      function warmGuest() { idle(function () { if (!state.options) loadOptions(); }); }
      if (!state.member) { warmGuest(); return; }
      // Members-only data waits on /balance saying they're in the club (the
      // shared cache probes /balance first anyway, so this costs nothing).
      loadBalance().then(function () {
        if (!state.member) { warmGuest(); return; }
        loadCodes();
        idle(function () { if (!state.dashboard) loadDashboard(); if (!state.options) loadOptions(); if (!state.referral) loadReferral(); });
      });
    }

    // ── shared markup ──────────────────────────────────────────────────────────
    function head(title, eyebrow) {
      return '<div class="kkl-head"><div class="kkl-head__txt">' +
        (eyebrow ? '<span class="kkl-head__eye">' + esc(eyebrow) + '</span>' : '') +
        '<span class="kkl-head__title">' + esc(title) + '</span></div>' +
        '<button type="button" class="kkl-x" aria-label="Close" data-kkl="close">' + ic('close', 18) + '</button></div>';
    }
    function dHead(title) {
      return '<div class="kkl-dhead"><button type="button" class="kkl-back" aria-label="Back" data-kkl="home">' + ic('back', 18) + '</button>' +
        '<span class="kkl-dhead__t">' + esc(title) + '</span></div>';
    }
    function footer(label) {
      return '<a class="kkl-foot" href="' + esc(safeUrl(CFG.clubUrl || '/pages/kkumi-club')) + '">' +
        '<span>' + esc(label || copy('footerLabel', 'Open the full Club page')) + '</span>' + ic('arrow', 18) + '</a>';
    }
    function gate(line) {
      return '<div class="kkl-gate"><span class="kkl-gate__line">' + esc(line) + '</span>' +
        '<a class="kkl-btn kkl-btn--block" href="' + esc(safeUrl(joinHref())) + '">' + esc(copy('joinCta', 'Join free')) + '</a></div>';
    }
    function loadingState(msg) {
      return '<div class="kkl-state"><div class="kkl-spinner" aria-hidden="true"></div><span class="kkl-state__t">' + esc(msg || copy('loadingDefault', 'Loading…')) + '</span></div>';
    }
    function errorState(view) {
      return '<div class="kkl-state kkl-state--error"><span class="kkl-state__t">' + esc(copy('errorText', 'Couldn’t load that. Please try again.')) + '</span>' +
        '<button type="button" class="kkl-btn kkl-btn--sm" data-kkl="retry" data-view="' + esc(view) + '">' + esc(copy('retryLabel', 'Retry')) + '</button></div>';
    }

    // The signed-in member's earn multiplier: /balance carries it, the program's
    // tier row is the fallback, and guests (or an unresolvable tier) get 1.
    function memberMultiplier() {
      var t = state.balance && state.balance.tier;
      if (!t) return 1;
      var m = Number(t.multiplier);
      if (!(m > 0) && t.id && state.tiersById[t.id]) m = Number(state.tiersById[t.id].multiplier);
      return m > 0 ? m : 1;
    }

    function earnRows() {
      var p = (state.program && state.program.points) || {};
      // The rate THIS member is on, matching the footer card's `earn-mine`
      // token and what kkumi-earn-points.js previews on the PDP/cart: an Idol
      // on a 1.5x tier reads 15 pts / $1, not the program base of 10.
      var R = p.earn_per_dollar != null ? +(Number(p.earn_per_dollar) * memberMultiplier()).toFixed(2) : null;
      var rows = [
        { icon: ic('tag', 19), t: copy('earnOrderTitle', 'Place an order'), s: copy('earnOrderSub', 'Your everyday earner'),
          pts: R != null ? copy('earnRateFmt', '{n} pts / $1').split('{n}').join(fmt(R)) : null },
        { icon: ic('star', 19), t: copy('earnReviewTitle', 'Write a review'), s: copy('earnReviewSub', 'Photos & videos earn more'),
          pts: p.review_bonus != null ? ('+' + fmt(p.review_bonus)) : null },
        { icon: ic('gift', 19), t: copy('earnBdayTitle', 'Birthday treat'), s: copy('earnBdaySub', 'Every year, on us'),
          pts: p.birthday_bonus != null ? ('+' + fmt(p.birthday_bonus)) : null },
        { icon: ic('users', 19), t: copy('earnReferTitle', 'Refer a friend'), s: copy('earnReferSub', 'They order, you both win'),
          pts: p.referral_bonus != null ? ('+' + fmt(p.referral_bonus)) : null },
      ];
      return rows.filter(function (r) { return r.pts != null; });
    }

    function tiles() {
      var ready = state.codes ? state.codes.length : (state.dashboard && state.dashboard.rewards ? state.dashboard.rewards.affordable : 0);
      var p = (state.program && state.program.points) || {};
      return [
        { key: 'earn', icon: tileIcon('svgEarn', 'spark'), t: copy('earnTitle', 'Ways to earn'), s: copy('tileEarnSub', 'Shop · review · refer') },
        { key: 'redeem', icon: tileIcon('svgRedeem', 'ticket'), t: copy('redeemTitle', 'Ways to redeem'), s: copy('tileRedeemSub', 'Trade points for perks') },
        { key: 'rewards', icon: tileIcon('svgRewards', 'heart'), t: copy('rewardsTitle', 'My rewards'), s: state.member ? (fmt(ready) + ' ' + copy('tileRewardsReady', 'ready to use')) : copy('tileRewardsGuestSub', 'Coupons & freebies'),
          badge: state.member && ready ? ready : '' },
        { key: 'refer', icon: tileIcon('svgRefer', 'users'), t: copy('referTitle', 'Refer a friend'), s: p.referral_bonus != null ? ('+' + fmt(p.referral_bonus) + ' ' + copy('tileReferEach', 'pts each')) : copy('tileReferSub', 'Give points, get points') },
      ];
    }
    // A tile's icon = the merchant's custom SVG (Theme settings, trusted store
    // config — the chip's `svg{width:20}` rule sizes it) or the built-in glyph.
    function tileIcon(cfgKey, fallback) {
      var svg = CFG[cfgKey];
      return (svg && /<svg[\s\S]*<\/svg>/i.test(svg)) ? svg : ic(fallback, 20);
    }
    function tileGrid(extraCls) {
      return '<div class="kkl-grid' + (extraCls || '') + '">' + tiles().map(function (a, i) {
        return '<button type="button" class="kkl-tile' + (extraCls ? ' kkl-tile--c' + (i % 4) : '') + '" data-kkl="view" data-view="' + a.key + '">' +
          '<span class="kkl-tile__ic">' + a.icon + '</span>' +
          '<span class="kkl-tile__badge">' + (a.badge ? esc(a.badge) : '') + '</span>' +
          '<span class="kkl-tile__t">' + esc(a.t) + '</span>' +
          '<span class="kkl-tile__s">' + esc(a.s) + '</span></button>';
      }).join('') + '</div>';
    }

    // ── render dispatch ────────────────────────────────────────────────────────
    function render() {
      hydrateFromCache();
      var html;
      if (state.view === 'earn') html = renderEarn();
      else if (state.view === 'redeem') html = renderRedeem();
      else if (state.view === 'rewards') html = renderRewards();
      else if (state.view === 'refer') html = renderRefer();
      else html = state.member ? renderDashboard() : renderHybrid();
      panelEl.innerHTML = html;
    }

    function renderDashboard() {
      // Resilient: render the shell (greeting/balance/tiles) from whatever we
      // already have, and only the pieces still loading get an inline spinner —
      // never a full-panel "loading" takeover.
      var d = state.dashboard || {};
      var name = (d.member && d.member.first_name) || '';
      var tier = d.tier || (state.balance && state.balance.tier) || null;
      var bal = balanceNow();
      var greet = '<div class="kkl-greet"><div class="kkl-greet__l">' +
        '<span class="kkl-greet__hi">' + (name ? tpl('greet', 'Hi, {name}', { name: esc(name) }) : esc(copy('greetAnon', 'Hi there'))) + '</span>' +
        (tier ? '<span class="kkl-badge">' + tierIcon(tier.id, 12) + '<span>' + esc(tier.name) + '</span></span>' : '') +
        '</div><button type="button" class="kkl-x" aria-label="Close" data-kkl="close">' + ic('close', 18) + '</button></div>';
      var balance = bal != null
        ? '<div class="kkl-balance"><span class="kkl-balance__n">' + fmt(bal) + '</span><span class="kkl-balance__u">' + esc(copy('balanceUnitTop', 'points')) + '<br>' + esc(copy('balanceUnitBot', 'available')) + '</span></div>'
        : '<div class="kkl-balance kkl-balance--load"><span class="kkl-spinner" aria-hidden="true"></span></div>';
      var rail = (d.tier && d.tier.progress) ? miniRail(tier)
        : (state.loading.dashboard ? '<div class="kkl-railload"><span class="kkl-spinner" aria-hidden="true"></span></div>'
        : (state.error.dashboard ? errorState('dashboard') : ''));
      return '<div class="kkl-body">' + greet + balance + rail + tileGrid('') + footer() + '</div>';
    }

    function miniRail(tier) {
      var pr = (state.dashboard && state.dashboard.tier && state.dashboard.tier.progress) || null;
      if (!pr) return '';
      var fromName = (tier && tier.name) || copy('railFrom', 'Your tier');
      if (pr.is_top) {
        return '<div class="kkl-rail"><div class="kkl-rail__ends">' +
          '<span class="kkl-rail__from">' + tierIcon(tier && tier.id, 14) + '<span>' + esc(fromName) + '</span></span>' +
          '<span class="kkl-rail__to">' + esc(copy('topTier', 'Top tier ✦')) + '</span></div>' +
          '<div class="kkl-rail__track"><div class="kkl-rail__fill" style="width:100%"></div><span class="kkl-rail__dot" style="left:100%"></span></div>' +
          '<div class="kkl-rail__cap">' + esc(copy('topTierCap', 'You’ve reached the top; enjoy every perk ✦')) + '</div></div>';
      }
      var pct = Math.max(2, Math.min(100, Number(pr.pct) || 0));
      var toGo = Math.max(0, (pr.next_tier_points || 0) - (pr.tier_points || 0));
      var perk = nextTierPerk(state.tiersById[pr.next_tier_id]);
      return '<div class="kkl-rail"><div class="kkl-rail__ends">' +
        '<span class="kkl-rail__from">' + tierIcon(tier && tier.id, 14) + '<span>' + esc(fromName) + '</span></span>' +
        '<span class="kkl-rail__to">' + tierIcon(pr.next_tier_id, 14) + '<span>' + esc(pr.next_tier_name || 'Next') + '</span></span></div>' +
        '<div class="kkl-rail__track"><div class="kkl-rail__fill" style="width:' + pct + '%"></div><span class="kkl-rail__dot" style="left:' + pct + '%"></span></div>' +
        '<div class="kkl-rail__cap"><b>' + fmt(toGo) + ' ' + esc(copy('ptsUnit', 'pts')) + '</b> ' + esc(copy('railTo', 'to')) + ' ' + esc(pr.next_tier_name || 'the next tier') + (perk ? ' · ' + esc(perk) : '') + '</div></div>';
    }
    function nextTierPerk(t) {
      if (!t) return '';
      var m = Number(t.multiplier) || 1;
      return m > 1 ? copy('railPerk', 'earn {pct}% more points').split('{pct}').join(Math.round((m - 1) * 100)) : '';
    }

    function renderHybrid() {
      var statcard = '<div class="kkl-statcard">' +
        '<div class="kkl-statcard__txt"><span class="kkl-statcard__big">' + esc(copy('joinTitle', 'Join the Club')) + '</span>' +
        '<span class="kkl-statcard__sub">' + esc(copy('joinSub', 'Earn points on every order & unlock perks.')) + '</span></div>' +
        '<a class="kkl-btn kkl-btn--sm" href="' + esc(safeUrl(joinHref())) + '">' + esc(copy('joinCta', 'Join free')) + '</a></div>';
      return '<div class="kkl-body">' + head(copy('hybridTitle', 'The Kkumi Club'), copy('eyebrow', 'your rewards')) + statcard + tileGrid(' kkl-grid--hybrid') + footer(copy('footerGuestLabel', 'See how it works')) + '</div>';
    }

    function renderEarn() {
      var bal = balanceNow();
      var lead = state.member && bal != null
        ? tpl('earnLeadMember', 'You’ve got {pts}. Here’s how to stack more.', { pts: ptsHtml(bal) })
        : state.member ? esc(copy('earnLeadMemberNobal', 'Here’s how to stack more points.'))
        : esc(copy('earnLeadGuest', 'Members earn points on everything below.'));
      var rows = earnRows();
      var body = (!state.program && !rows.length) ? loadingState(copy('loadingEarn', 'Loading earn rates…'))
        : '<div class="kkl-list">' + rows.map(function (e) {
            return '<div class="kkl-erow"><span class="kkl-row__ic">' + e.icon + '</span>' +
              '<span class="kkl-row__txt"><span class="kkl-row__t">' + esc(e.t) + '</span><span class="kkl-row__s">' + esc(e.s) + '</span></span>' +
              '<span class="kkl-pts-tag">' + esc(e.pts) + '</span></div>';
          }).join('') + '</div>';
      return '<div class="kkl-body kkl-detail">' + dHead(copy('earnTitle', 'Ways to earn')) +
        '<p class="kkl-lead">' + lead + '</p>' + body +
        (state.member ? footer(copy('earnFooter', 'Full earning guide')) : gate(copy('earnGate', 'Join free to start earning on every order.'))) + '</div>';
    }

    function renderRedeem() {
      var inner;
      if (state.member && state.loading.options && !state.options) inner = loadingState(copy('loadingRedeem', 'Loading rewards…'));
      else if (state.member && state.error.options) inner = errorState('options');
      else inner = redeemList();
      var bal = balanceNow();
      var lead = !state.member ? esc(copy('redeemLeadGuest', 'Turn points into perks once you join.'))
        : (bal != null ? tpl('redeemLeadMember', 'You have {pts} to spend.', { pts: ptsHtml(bal) }) : esc(copy('redeemLeadMemberNobal', 'Redeem your points for perks below.')));
      return '<div class="kkl-body kkl-detail">' + dHead(copy('redeemTitle', 'Ways to redeem')) +
        '<p class="kkl-lead">' + lead + '</p>' + inner +
        (!state.member ? gate(copy('redeemGate', 'Join free; your points start adding up right away.')) : '') + '</div>';
    }
    // ── the shared points drawer (KKPointsSheet) ─────────────────────────────
    // Redeem hands off to the SAME side drawer the cart and the account Kkumi
    // Club tab use (assets/kkumi-points-sheet.js + kkumi-points-kit.css): the
    // member picks the flavour / colour there, sees balance → after, confirms.
    // Both files are loaded on demand (URLs from the launcher snippet) so pages
    // that never redeem don't pay for them; the cart and account pages already
    // carry them.
    var sheetP = null;
    function ensureSheet() {
      if (window.KKPointsSheet && document.querySelector('link[href*="kkumi-points-kit"]')) return Promise.resolve(window.KKPointsSheet);
      if (sheetP) return sheetP;
      sheetP = new Promise(function (resolve, reject) {
        if (CFG.sheetCss && !document.querySelector('link[href*="kkumi-points-kit"]')) {
          var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CFG.sheetCss; document.head.appendChild(l);
        }
        if (window.KKPointsSheet) { resolve(window.KKPointsSheet); return; }
        if (!CFG.sheetJs) { reject(new Error('no_sheet')); return; }
        var sc = document.createElement('script'); sc.src = CFG.sheetJs; sc.async = true;
        sc.onload = function () { window.KKPointsSheet ? resolve(window.KKPointsSheet) : reject(new Error('no_sheet')); };
        sc.onerror = function () { sheetP = null; reject(new Error('no_sheet')); };
        document.head.appendChild(sc);
      });
      return sheetP;
    }
    // Drawer copy: theme settings → Kkumi Club Launcher → "Redeem drawer",
    // handed over as CFG.sheet (keys are the drawer's own: shRedeemTitle, pts…).
    function sc(key, fallback) { var v = (CFG.sheet || {})[key]; return v != null && v !== '' ? v : fallback; }
    function st(key, fallback, map) {
      var out = esc(sc(key, fallback));
      if (map) Object.keys(map).forEach(function (k) { out = out.split('[' + k + ']').join(esc(map[k])); });
      return out;
    }
    // The drawer's state object (balance / rows / busy / sheet), refreshed from
    // the launcher's state each time it opens.
    var SH = { balance: 0, tier: null, rows: [], tiers: [], creditOpts: [], credit: null, busy: null, sheet: null };
    var sheet = null;
    function sheetRowByKey(k) { return SH.rows.filter(function (r) { return r.key === k; })[0] || null; }
    function getSheet(P) {
      if (sheet) return sheet;
      sheet = P.create({
        state: function () { return SH; },
        c: sc, t: st,
        rowByKey: sheetRowByKey,
        rowState: function () { return { s: 'can' }; },
        thumb: P.thumb,
        worth: function (o) { return P.worth(o, st); },
        tierIcon: function (id) { return P.tierIcon(id, SH.tiers); },
        action: function () { return ''; },
        render: function () { if (SH.sheet) sheet.draw(); },
        onRedeemed: function (res, o, row, picked) { SH.busy = null; afterRedeemed(res, picked, P); },
        onStale: function () {
          SH.busy = null;
          toast(esc(copy('errorInsufficient', 'You don’t have enough points for that yet.')));
          if (window.KKRewards) window.KKRewards.invalidate();
          refreshAfterRedeem();
        },
        onConverted: function () {}, onCreditPending: function () {}
      });
      return sheet;
    }
    function openRedeemSheet(rowKey) {
      ensureSheet().then(function (P) {
        var s = getSheet(P);
        SH.balance = balanceNow() || 0;
        SH.tier = state.balance && state.balance.tier || null;
        SH.tiers = (state.program && state.program.tiers) || [];
        SH.rows = P.rowsFrom(state.options || []);
        if (!sheetRowByKey(rowKey)) return;
        // The drawer is a page-level modal: put the launcher away first, and
        // return focus to the FAB when the drawer closes.
        setOpen(false);
        s.startRedeem(rowKey, fabEl);
      }).catch(function () { toast(esc(copy('errorGeneric', 'Sorry, that didn’t work. Please try again.'))); });
    }

    // Reward rows: the cart / account list in the launcher's width — grouped
    // with KKPointsSheet.rowsFrom when it's loaded (rewards sharing a
    // `group_key` are ONE row; the drawer offers the flavours), the product
    // photo, "750 pts · free item", and one action. The drawer script is warmed
    // with the options, so this twin of rowsFrom only covers the first paint
    // before it lands.
    function launcherRows(opts) {
      if (window.KKPointsSheet) return window.KKPointsSheet.rowsFrom(opts);
      var rows = [], byKey = {};
      opts.forEach(function (o) {
        if (o.type === 'variable' || o.type === 'variable_credit' || o.type === 'fixed_credit') return;
        var key = o.group_key ? 'g:' + o.group_key : 'r:' + o.id;
        var row = byKey[key];
        if (!row) { row = byKey[key] = { key: key, members: [] }; rows.push(row); }
        row.members.push(o);
      });
      rows.forEach(function (row) {
        var labelled = row.members.filter(function (m) { return m.group_label; })[0];
        row.title = row.members.length > 1 ? ((labelled && labelled.group_label) || row.members[0].label) : row.members[0].label;
        row.min = Math.min.apply(null, row.members.map(function (m) { return Number(m.points) || 0; }));
        row.image = (row.members.filter(function (m) { return m.image_url; })[0] || {}).image_url || null;
        row.locked = row.members.every(function (m) { return m.locked; });
        var gate = row.members.filter(function (m) { return m.locked; })[0] || row.members[0];
        row.tierName = gate.min_tier_name || '';
      });
      return rows;
    }
    function rowWorth(o) {
      if (window.KKPointsSheet) return window.KKPointsSheet.worth(o, st);
      if (o.type === 'percent' && o.percent_off) return st('percent', '[pct]% off', { pct: o.percent_off });
      if (o.type === 'free_product' || o.type === 'free_collection' || o.type === 'bogo') return st('free', 'free item');
      return '';
    }
    function rowThumb(row) {
      if (row.image) return '<img class="kkl-rrow__img kk-img-skel" src="' + esc(row.image) + '" alt="" loading="lazy" decoding="async" width="44" height="44">';
      return '<span class="kkl-rrow__img kkl-rrow__img--glyph">' + ic('tag', 19) + '</span>';
    }
    function redeemList() {
      if (!state.member && !state.options && !state.loading.options && !state.error.options) { loadOptions(); return loadingState(copy('loadingPerks', 'Loading perks…')); }
      if (!state.member && state.loading.options && !state.options) return loadingState(copy('loadingPerks', 'Loading perks…'));
      var rows = launcherRows((state.options || []).filter(function (o) { return o.points != null; }));
      if (!rows.length) return '<div class="kkl-empty"><span class="kkl-empty__t">' + esc(copy('redeemEmptyTitle', 'No rewards yet')) + '</span><span class="kkl-empty__s">' + esc(copy('redeemEmptySub', 'New perks are on the way. Check back soon.')) + '</span></div>';
      var bal = balanceNow() || 0;
      // Redeemable first, then cheapest first — the cart's order.
      var can = function (r) { return state.member && r.members.some(function (m) { return !m.locked && (Number(m.points) || 0) <= bal; }); };
      rows = rows.slice().sort(function (a, b) { return (can(b) - can(a)) || (a.min - b.min); });
      return '<div class="kkl-list">' + rows.map(function (row) {
        var ok = can(row);
        var right;
        if (!state.member) right = '<span class="kkl-rrow__lock">' + ic('lock', 15) + '</span>';
        else if (row.locked) right = '<span class="kkl-rrow__need">' + st('tierOnly', '[tier] only', { tier: row.tierName || copy('lockedLabel', 'Locked') }) + '</span>';
        else if (ok) right = '<button type="button" class="kkl-btn kkl-btn--sm kkl-rrow__btn" data-kkl="redeem" data-row="' + esc(row.key) + '"' +
          ' aria-label="' + st('redeemAria', 'Redeem [name] for [n] points', { name: row.title, n: fmt(row.min) }) + '">' + esc(copy('redeemCta', 'Redeem')) + '</button>';
        else right = '<span class="kkl-rrow__need">' + st('toGo', '[n] pts to go', { n: fmt(row.min - bal) }) + '</span>';
        var extra = rowWorth(row.members[0]);
        return '<div class="kkl-rrow' + (state.member && !ok ? ' is-locked' : '') + '">' + rowThumb(row) +
          '<span class="kkl-row__txt"><span class="kkl-row__t">' + esc(row.title) + '</span>' +
          '<span class="kkl-row__s"><b>' + fmt(row.min) + '</b> ' + st('pts', 'pts') + (extra ? ' · ' + extra : '') + '</span></span>' +
          '<span class="kkl-rrow__act">' + right + '</span></div>';
      }).join('') + '</div>';
    }

    function renderRewards() {
      if (!state.member) {
        return '<div class="kkl-body kkl-detail">' + dHead(copy('rewardsTitle', 'My rewards')) +
          '<div class="kkl-empty"><span class="kkl-empty__ic">' + ic('heart', 26) + '</span>' +
          '<span class="kkl-empty__t">' + esc(copy('rewardsEmptyTitle', 'No rewards yet')) + '</span>' +
          '<span class="kkl-empty__s">' + esc(copy('rewardsGuestEmptySub', 'Join the Club and your coupons & freebies land right here.')) + '</span></div>' +
          gate(copy('rewardsGate', 'Join free to start collecting rewards.')) + '</div>';
      }
      var inner;
      if (state.loading.codes && !state.codes) inner = loadingState(copy('loadingRewards', 'Loading your rewards…'));
      else if (state.error.codes) inner = errorState('codes');
      else {
        var codes = state.codes || [];
        if (!codes.length) {
          inner = '<div class="kkl-empty"><span class="kkl-empty__ic">' + ic('heart', 26) + '</span>' +
            '<span class="kkl-empty__t">' + esc(copy('rewardsEmptyTitle', 'No rewards yet')) + '</span>' +
            '<span class="kkl-empty__s">' + esc(copy('rewardsEmptySub', 'Redeem your points and your coupons appear here, ready to use.')) + '</span></div>' +
            '<a class="kkl-btn kkl-btn--block" href="#" data-kkl="view" data-view="redeem">' + esc(copy('rewardsEmptyCta', 'See ways to redeem')) + '</a>';
        } else {
          var nCodes = codes.length;
          var countLine = nCodes === 1
            ? tpl('rewardsCountOne', '{n} reward ready to use.', { n: '<b>' + fmt(nCodes) + '</b>' })
            : tpl('rewardsCountMany', '{n} rewards ready to use.', { n: '<b>' + fmt(nCodes) + '</b>' });
          inner = '<p class="kkl-lead">' + countLine + '</p>' +
            '<div class="kkl-coupons">' + codes.map(function (c) {
              var dollars = c.discount_cents != null ? Math.floor(Number(c.discount_cents) / 100) : null;
              var isFree = c.reward_type === 'free_product' || c.reward_type === 'free_collection';
              var val = isFree ? copy('couponFree', 'FREE') : (c.reward_type === 'percent' && c.percent_off ? (c.percent_off + '%') : (dollars != null ? ('$' + dollars) : copy('couponReward', 'REWARD')));
              var unit = isFree ? copy('couponGift', 'GIFT') : (c.reward_type === 'percent' ? copy('couponOff', 'OFF') : (dollars != null ? copy('couponOff', 'OFF') : copy('couponCode', 'CODE')));
              var note = c.reward_label || c.product_title || copy('couponNote', 'Your reward');
              var use = BASE + '/discount/' + encodeURIComponent(c.discount_code) + '?redirect=' + encodeURIComponent(BASE + '/cart');
              return '<div class="kkl-coupon"><div class="kkl-coupon__l">' +
                '<span class="kkl-coupon__val">' + esc(val) + ' <small>' + esc(unit) + '</small></span>' +
                '<span class="kkl-coupon__note">' + esc(note) + '</span></div>' +
                '<div class="kkl-coupon__r"><span class="kkl-coupon__code">' + esc(c.discount_code) + '</span>' +
                '<a class="kkl-btn kkl-btn--sm" href="' + esc(safeUrl(use)) + '">' + esc(copy('useLabel', 'Use')) + '</a></div></div>';
            }).join('') + '</div>';
        }
      }
      return '<div class="kkl-body kkl-detail">' + dHead(copy('rewardsTitle', 'My rewards')) + inner + '</div>';
    }

    function renderRefer() {
      var p = (state.program && state.program.points) || {};
      var bonus = p.referral_bonus;
      var big = bonus != null
        ? rawTpl('referBig', 'Give {give}<br>get {get} pts.', { give: '<em>' + fmt(bonus) + '</em>', get: '<em>' + fmt(bonus) + '</em>' })
        : rawTpl('referBigFallback', 'Refer a<br><em>friend</em>.');
      var sub = copy('referSub', 'They get a welcome gift on their first order. You earn points when they buy.');
      var headHtml = '<div class="kkl-refer"><span class="kkl-refer__big">' + big + '</span><span class="kkl-refer__sub">' + esc(sub) + '</span></div>';
      var body;
      if (!state.member) {
        body = gate(copy('referGate', 'Join free to get your referral link.'));
      } else if (state.loading.referral && !state.referral) {
        body = loadingState(copy('loadingRefer', 'Loading your link…'));
      } else if (state.error.referral) {
        body = errorState('referral');
      } else {
        var r = state.referral || {};
        var stat = '';
        if (r.friends_joined != null) {
          var fj = r.friends_joined;
          stat = '<p class="kkl-refer__stat">' + (fj === 1
            ? tpl('referStatOne', '★ {friends} friend joined · {points} pts earned', { friends: fmt(fj), points: '<b>' + fmt(r.points_earned || 0) + '</b>' })
            : tpl('referStatMany', '★ {friends} friends joined · {points} pts earned', { friends: fmt(fj), points: '<b>' + fmt(r.points_earned || 0) + '</b>' })) + '</p>';
        }
        body = '<div class="kkl-codebox"><span class="kkl-codebox__code">' + esc(r.referral_url || '—') + '</span>' +
          '<button type="button" class="kkl-codebox__copy" data-kkl="copy">' + ic('copy', 15) + ' ' + esc(copy('referCopy', 'Copy')) + '</button></div>' +
          '<button type="button" class="kkl-btn kkl-btn--block" data-kkl="share">' + ic('share', 16) + ' ' + esc(copy('referShare', 'Share your link')) + '</button>' + stat;
      }
      return '<div class="kkl-body kkl-detail">' + dHead(copy('referTitle', 'Refer a friend')) + headHtml + body + '</div>';
    }

    // ── redeem action ──────────────────────────────────────────────────────────

    // Redemption-flow toast copy. The redeemed heading / store-credit body /
    // discount-code body+action / insufficient + generic errors are SHARED with
    // the club page (theme settings → "Kkumi Club"), fed into CFG via the
    // launcher snippet so a merchant edits them once. tier_locked is launcher-
    // specific.
    // A trailing `→` in the action label becomes the shared arrow SVG;
    // KKIcons.labelHTML() escapes the label itself (JS twin of
    // snippets/kkumi-label-arrow.liquid). Falls back to plain escaped text.
    function actionHTML(label) {
      return (window.KKIcons && window.KKIcons.labelHTML) ? window.KKIcons.labelHTML(label) : esc(label);
    }
    // `bodyCopy` overrides the "use this code at checkout" line — the add-to-cart
    // failure path swaps in its own sentence rather than stacking a second one
    // on top of this toast's.
    function codeToast(code, bodyCopy) {
      var use = BASE + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(BASE + '/cart');
      return esc(copy('redeemedHeading', 'Redeemed ✓')) + ' ' + esc(bodyCopy || copy('codeBody', 'Use this code at checkout:')) +
        ' <b>' + esc(code) + '</b> · <a href="' + esc(safeUrl(use)) + '">' + actionHTML(copy('codeAction', 'Use it now →')) + '</a>';
    }
    // After the drawer's POST /redeem: free-item rewards go into the cart with
    // the code attached and land on /cart; anything else toasts its code.
    function afterRedeemed(res, pickedId, P) {
      var wanted = P.itemsToAdd(res, pickedId);
      if (!wanted.length) { toast(codeToast(res.discount_code)); refreshAfterRedeem(); return; }
      toast(esc(copy('addingHeading', 'Adding to cart…')));
      cartPost('/cart/add.js', { items: wanted })
        .then(function (added) {
          var got = addedVariantIds(added);
          if (wanted.some(function (w) { return got.indexOf(String(w.id)) === -1; })) throw new Error('cart_add_incomplete');
          return cartPost('/cart/update.js', { discount: res.discount_code });
        })
        .then(function () { window.location.href = BASE + '/cart'; })
        .catch(function (err) {
          if (window.console && console.warn) {
            console.warn('[kkumi] reward redeemed but not added to cart', {
              variants: wanted.map(function (w) { return w.id; }),
              code: res.discount_code,
              reason: (err && (err.cartError || err.message)) || 'unknown',
            });
          }
          toast(codeToast(res.discount_code, copy('addFailedBody', 'We couldn’t add it to your cart — use this code at checkout instead:')));
          refreshAfterRedeem();
        });
    }
    function refreshAfterRedeem() {
      // postApi already invalidated the shared cache; pull fresh copies.
      state.dashboard = null; state.codes = null;
      loadBalance(); loadCodes(); loadOptions(); if (state.open) loadDashboard();
      // Tell the page's club sections (cart wallet, club page) the balance
      // moved — they hold their own copy and would otherwise show the
      // pre-redemption number until a reload. Twin of announcePointsChanged()
      // in assets/kkumi-club.js; listeners refetch and never re-dispatch.
      try { document.dispatchEvent(new CustomEvent('kkumi:points-changed', { detail: { source: 'launcher' } })); } catch (e) {}
    }

    // POST to a storefront cart AJAX endpoint, rejecting on a refusal. Twin of
    // cartPost() in assets/kkumi-club.js — Shopify answers a rejected
    // /cart/add.js with an HTTP error and a JSON `description`, which fetch()
    // does not reject on, so an unchecked chain applies the discount and
    // redirects to a cart that never received the reward.
    function cartPost(path, body) {
      return fetch(BASE + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json().catch(function () { return null; }).then(function (j) {
          if (!r.ok) {
            var e = new Error((j && (j.description || j.message)) || ('HTTP ' + r.status));
            e.cartError = (j && j.description) || null;
            throw e;
          }
          return j;
        });
      });
    }
    // Every variant /cart/add.js reports back, as strings — a 200 holding none
    // of the ids we asked for means nothing landed.
    function addedVariantIds(res) {
      var list = (res && res.items) ? res.items : (res && res.variant_id != null ? [res] : []);
      return list.map(function (i) { return String(i.variant_id != null ? i.variant_id : i.id); });
    }

    function doCopy(btn) {
      var r = state.referral || {};
      if (!r.referral_url) return;
      if (navigator.clipboard) navigator.clipboard.writeText(r.referral_url).catch(function () {});
      var orig = btn.innerHTML;
      btn.innerHTML = ic('copy', 15) + ' ' + esc(copy('referCopied', 'Copied!'));
      setTimeout(function () { btn.innerHTML = orig; }, 1500);
    }
    function doShare(btn) {
      var r = state.referral || {};
      if (!r.referral_url) return;
      if (navigator.share) navigator.share({ title: copy('shareTitle', 'Join me at Kkumi'), url: r.referral_url }).catch(function () {});
      else if (navigator.clipboard) { navigator.clipboard.writeText(r.referral_url).catch(function () {}); toast(esc(copy('shareCopiedToast', 'Referral link copied!'))); }
      else toast(tpl('shareLinkToast', 'Your link: {link}', { link: '<b>' + esc(r.referral_url) + '</b>' }));
    }

    // ── events ──────────────────────────────────────────────────────────────────
    rootEl.addEventListener('click', function (ev) {
      var el = ev.target.closest('[data-kkl]');
      if (!el || !rootEl.contains(el)) return;
      var action = el.getAttribute('data-kkl');
      if (action === 'toggle') setOpen(!state.open);
      else if (action === 'close') setOpen(false);
      else if (action === 'home') go('home');
      else if (action === 'view') { ev.preventDefault(); go(el.getAttribute('data-view')); }
      else if (action === 'retry') { var v = el.getAttribute('data-view'); ({ dashboard: loadDashboard, options: loadOptions, codes: loadCodes, referral: loadReferral }[v] || function () {})(); }
      else if (action === 'redeem') openRedeemSheet(el.getAttribute('data-row'));
      else if (action === 'copy') doCopy(el);
      else if (action === 'share') doShare(el);
    });
    document.addEventListener('keydown', function (ev) { if (ev.key === 'Escape' && state.open) setOpen(false); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible' || !state.member) return;
      // Re-probe via the shared cache; pull fresh copies of what we hold.
      if (window.KKRewards) window.KKRewards.invalidate();
      state.dashboard = null; state.referral = null;
      loadBalance(); loadCodes(); idle(loadOptions);
      if (state.open) loadDashboard();
    });
    // A redemption elsewhere on the page (club page, cart wallet) moves the
    // balance this FAB's points pill renders — refresh instead of waiting for
    // a reload or a tab switch. Refetch only; never re-dispatch.
    document.addEventListener('kkumi:points-changed', function (ev) {
      if (!state.member) return;
      if (ev && ev.detail && ev.detail.source === 'launcher') return;  // our own announce already refreshed
      if (window.KKRewards) window.KKRewards.invalidate();
      state.dashboard = null; state.codes = null;
      loadBalance(); loadCodes(); idle(loadOptions);
      if (state.open) loadDashboard();
    });

    // ── auto-hide on scroll ────────────────────────────────────────────────────
    // Two distinct behaviors, both driven off scroll position:
    //
    // 1. Collection pages (drawer mode, ≤1089px): the sticky filter/sort bar
    //    (sections/kkumi-collection-grid.liquid #kkumi-sticky-filter) is fixed
    //    bottom-right for the WHOLE session there, not just while scrolled — so
    //    the dock has no bottom-edge room to share with it at all, and the bar
    //    wins: the dock is hidden for the whole page, at every scroll position.
    //    This used to reveal the dock in the top 80px "before there's anything
    //    to overlap" — a false premise, since the bar is on screen from y=0, so
    //    the FAB simply sat on top of the bar's Sort button (z-index 950 against
    //    the bar's 60) and swallowed the tap until the shopper scrolled past the
    //    threshold. Lifting or re-anchoring the dock instead was tried and
    //    rejected earlier (it crowded the bar or fought the panel's open
    //    direction), so the club entry point is simply not offered on these
    //    pages; it is unchanged everywhere else. Drives a dedicated
    //    `kkl-coll-hidden` <body> class (CSS:
    //    assets/section-kkumi-collection-grid.css) so it never entangles with
    //    the generic behavior below or the promo card, which keeps its own.
    //
    // 2. Everywhere else, small screens only (≤600px): the dock + the promo
    //    offers card take up a lot of the bottom of a small viewport. Tuck them
    //    off the bottom edge while the shopper scrolls DOWN, and bring them back
    //    on scroll-UP or once scrolling stops — so the content has the bottom to
    //    itself while browsing. Drives the (pre-existing) `kkl-scroll-hidden`
    //    <body> class, which moves both the dock and the separate promo card
    //    (CSS scopes the actual translate to the mobile breakpoint).
    function installScrollHide() {
      // Motion-sensitive shoppers opt out of behavior 2 — nothing slides. They
      // do NOT opt out of behavior 1: hiding the dock on collection pages is a
      // visibility decision that keeps the Sort button reachable, not an
      // animation, and returning early here left those shoppers with a FAB
      // permanently parked on top of it. (The transition itself is already
      // neutralised by the prefers-reduced-motion block in the CSS.)
      var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var mqMobile = window.matchMedia ? window.matchMedia('(max-width: 600px)') : { matches: false };
      var mqDrawer = window.matchMedia ? window.matchMedia('(max-width: 1089px)') : { matches: false };
      var lastY = window.pageYOffset || 0;
      var hidden = false, collHidden = false, ticking = false, idleTimer;
      function setHidden(next) {
        if (next === hidden) return;
        hidden = next;
        document.body.classList.toggle('kkl-scroll-hidden', next);
      }
      function setCollHidden(next) {
        if (next === collHidden) return;
        collHidden = next;
        document.body.classList.toggle('kkl-coll-hidden', next);
      }
      function onCollectionPage() {
        return mqDrawer.matches && !!document.getElementById('kkumi-sticky-filter');
      }
      function frame() {
        ticking = false;
        var y = window.pageYOffset || 0, dy = y - lastY;
        lastY = y;
        if (onCollectionPage()) {
          // Hidden for the whole page, at every scroll position — the sticky bar
          // owns this corner. The `!state.open` guard only matters when a panel
          // opened before a resize into drawer mode: it would otherwise vanish
          // under the shopper's thumb.
          setCollHidden(!state.open);
          setHidden(false);
          return;
        }
        setCollHidden(false);
        // Never hide on desktop, while the panel is open, or near the top.
        if (reduceMotion || !mqMobile.matches || state.open || y < 80) { setHidden(false); return; }
        if (dy > 6) setHidden(true);        // scrolling down → tuck away
        else if (dy < -6) setHidden(false); // scrolling up → bring back
        clearTimeout(idleTimer);            // stopped scrolling → restore shortly
        idleTimer = setTimeout(function () { setHidden(false); }, 900);
      }
      function schedule() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(frame);
      }
      window.addEventListener('scroll', schedule, { passive: true });
      // Resize/rotate too: onCollectionPage() is viewport-dependent, so without
      // this the class goes stale when a shopper rotates into or out of drawer
      // mode and never corrects until they happen to scroll.
      window.addEventListener('resize', schedule, { passive: true });
      // Evaluate once now. The listeners alone left the dock on screen — and,
      // on a collection page, over the bar — until the first scroll event.
      frame();
    }
    installScrollHide();

    // ── go ────────────────────────────────────────────────────────────────────
    mount();
    // Instant first paint from last-known cache, then warm/refresh.
    if (state.member) {
      var pb = peek('/balance'); applyClub(pb); if (pb && pb.logged_in) state.balance = pb;
      var pc = peek('/codes'); if (pc && pc.codes) state.codes = pc.codes;
      renderFab();
    }
    loadProgram();
    prefetch();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

